import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import type { LatestVersionDto } from '@monorepo/shared';
import { hasPermission, Permission } from '@monorepo/shared';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { useUserCompaniesStore } from '../stores/userCompanies.store';
import { useListLatestVersions, useGetVersionPresignedUrl } from '../services/documents.service';
import { LatestVersionsTable } from '../components/documents/LatestVersionsTable';
import {
  UploadNewVersionModal,
  type UploadNewVersionModalRef,
} from '../components/documents/UploadNewVersionModal';

export const LatestVersionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const companies = useUserCompaniesStore((state) => state.companies);
  const currentUserRole = companies.find((c) => c.id === selectedCompanyId)?.role;
  const uploadNewVersionModalRef = useRef<UploadNewVersionModalRef>(null);

  const canReupload = currentUserRole
    ? hasPermission(currentUserRole, Permission.UPLOAD_NEW_VERSION)
    : false;

  const { data, isLoading } = useListLatestVersions(selectedCompanyId ?? '');
  const getVersionPresignedUrlMutation = useGetVersionPresignedUrl(selectedCompanyId ?? '');

  const handleDownload = async (version: LatestVersionDto) => {
    try {
      const result = await getVersionPresignedUrlMutation.mutateAsync({
        documentId: version.documentId,
        versionId: version.versionId,
      });
      window.open(result.url, '_blank');
    } catch (error) {
      console.error('Error downloading version:', error);
    }
  };

  const handleReupload = (documentId: string, name: string) => {
    uploadNewVersionModalRef.current?.open(documentId, name);
  };

  const handleNavigateToFolder = (folderId: string) => {
    navigate(`/documents/${folderId}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        onClick={() => navigate('/documents')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={20} weight="regular" />
        {t('documents.back_to_documents')}
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-1">
        {t('documents.latest_versions_page.title')}
      </h1>
      <p className="text-sm text-gray-500 mb-6">{t('documents.latest_versions_page.subtitle')}</p>

      <LatestVersionsTable
        versions={data?.versions ?? []}
        isLoading={isLoading}
        canReupload={canReupload}
        isDownloading={getVersionPresignedUrlMutation.isPending}
        onDownload={handleDownload}
        onReupload={handleReupload}
        onNavigateToFolder={handleNavigateToFolder}
      />

      <UploadNewVersionModal ref={uploadNewVersionModalRef} companyId={selectedCompanyId ?? ''} />
    </div>
  );
};
